import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, AlertCircle, Clock, CheckCircle, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { Button } from './ui/button';

/**
 * Dashboard widget that shows a quick breakdown of pending loyalty memberships
 * categorized by remaining grace period. Hidden when no pending memberships.
 */
const PendingMembershipsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/loyalty/pending-memberships-summary');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading || !data || data.total === 0) return null;

  const goToLoyalty = () => navigate('/loyalty-admin');

  const expiredCount = data.expired.length;
  const criticalCount = data.critical.length;
  const urgentCount = data.urgent.length;
  const safeCount = data.safe.length;

  return (
    <div
      className="bg-card border border-amber-500/30 rounded-xl p-4 shadow-lg"
      data-testid="pending-memberships-widget"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-500" />
          <h3 className="font-bold">عضويات الولاء المعلّقة</h3>
          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold">
            {data.total}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="text-primary"
          onClick={goToLoyalty}
          data-testid="goto-loyalty-admin"
        >
          إدارة <ChevronRight className="w-4 h-4 ms-1" />
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {expiredCount > 0 && (
          <SummaryCell
            icon={<AlertCircle className="w-4 h-4" />}
            count={expiredCount}
            label="انتهت فترة السماح"
            sub="الحلاقون محظورون"
            colorClass="bg-red-700/15 border-red-700/40 text-red-300"
            pulse
            testid="cell-expired"
          />
        )}
        {criticalCount > 0 && (
          <SummaryCell
            icon={<AlertCircle className="w-4 h-4" />}
            count={criticalCount}
            label="حرجة (<1 يوم)"
            sub="يجب الاعتماد اليوم"
            colorClass="bg-red-500/15 border-red-500/40 text-red-300"
            pulse
            testid="cell-critical"
          />
        )}
        {urgentCount > 0 && (
          <SummaryCell
            icon={<Clock className="w-4 h-4" />}
            count={urgentCount}
            label="عاجلة (1-2 يوم)"
            sub="اعتمد قريباً"
            colorClass="bg-orange-500/15 border-orange-500/40 text-orange-300"
            pulse
            testid="cell-urgent"
          />
        )}
        {safeCount > 0 && (
          <SummaryCell
            icon={<CheckCircle className="w-4 h-4" />}
            count={safeCount}
            label="آمنة"
            sub={`>2 يوم متبقية`}
            colorClass="bg-amber-500/10 border-amber-500/30 text-amber-300"
            testid="cell-safe"
          />
        )}
      </div>

      {/* Top critical entries (max 3) */}
      {(expiredCount > 0 || criticalCount > 0) && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
          <p className="text-[11px] text-muted-foreground mb-1">الأولوية القصوى:</p>
          {[...data.expired, ...data.critical].slice(0, 3).map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between text-xs bg-secondary/20 rounded px-2 py-1.5"
              data-testid={`pending-row-${m.id}`}
            >
              <span className="font-mono">#{m.membership_number}</span>
              <span className="truncate max-w-[180px]">{m.name}</span>
              <span
                className={`font-bold ${
                  m.grace_remaining <= 0 ? 'text-red-400' : 'text-orange-400'
                }`}
              >
                {m.grace_remaining <= 0
                  ? '⛔ منتهية'
                  : `${m.grace_remaining}d متبقي`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SummaryCell = ({ icon, count, label, sub, colorClass, pulse, testid }) => (
  <div
    className={`p-3 rounded-lg border ${colorClass} ${pulse ? 'animate-pulse' : ''}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 mb-1">
      {icon}
      <span className="text-2xl font-bold">{count}</span>
    </div>
    <p className="text-[11px] font-semibold">{label}</p>
    <p className="text-[10px] opacity-80">{sub}</p>
  </div>
);

export default PendingMembershipsWidget;
